// @need-translate
import {HTMLAttributes, useCallback, useEffect, useRef, useState} from 'react';
import {IniErrorCode} from '../../../../../../common/shared/ini-player/IniErrorCode.enum';
import {IniExtendedErrorCode} from '../../../../../../common/shared/ini-player/IniExtendedErrorCode.enum';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {Loading} from '../../../../../../design-system/atoms/Loading/Loading';
import {Text} from '../../../../../../design-system/atoms/Text/Text';
import {
  CAN_RETRY_VIDEO_ERROR_LIST,
  INI_ERROR_MESSAGE_MAP,
  UNKNOWN_ERROR_MESSAGE,
} from '../constant/videoViewerConstant';
import {clearIniError} from '../state/videoViewerSlice';

export interface VideoPlayerIniErrorHandleContainerProps
  extends HTMLAttributes<HTMLDivElement> {
  productId: number;
  url: string;
  retryIniPlayer: () => void;
}

export function VideoPlayerIniErrorHandleContainer({
  productId,
  url,
  retryIniPlayer,
  className,
  ...props
}: VideoPlayerIniErrorHandleContainerProps) {
  const {iniError} = useTypedSelector(state => ({
    iniError: state.videoViewer.iniError,
  }));
  const dispatch = useTypedDispatch();
  const [isRetrying, setIsRetrying] = useState(false);
  const [errorInfo, setErrorInfo] = useState<{
    code?: IniErrorCode;
    iniExtendedCode?: IniExtendedErrorCode;
  }>();
  const retryTimer = useRef(0);

  const onClickRetry = useCallback(() => {
    setIsRetrying(true);
    dispatch(clearIniError());
    retryIniPlayer();
    retryTimer.current = window.setTimeout(() => {
      setIsRetrying(false);
    }, 2000);
  }, [dispatch, retryIniPlayer]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: url 변경마다 초기화 되어야함
  useEffect(() => {
    return () => window.clearTimeout(retryTimer.current);
  }, [url]);

  useEffect(() => {
    if (iniError) {
      setErrorInfo({
        code: iniError.code,
        iniExtendedCode: iniError.iniExtendedCode,
      });
    }
  }, [iniError]);

  useEffect(() => {
    return () => {
      dispatch(clearIniError());
    };
  }, [dispatch]);

  // ini가 존재하거나, retry 중이라면 에러 메시지를 보여줘야 한다.
  if (!(iniError || isRetrying)) {
    return null;
  }

  const canRetry =
    !errorInfo?.iniExtendedCode ||
    CAN_RETRY_VIDEO_ERROR_LIST.includes(errorInfo.iniExtendedCode);

  return (
    <div
      className={twMerge(
        'flex h-full w-full flex-col items-center justify-center bg-static-mono-black px-15pxr',
        className
      )}
      {...props}
    >
      <Text className="text-center whitespace-pre-line font-medium1-bold py-4pxr text-static-mono-10">
        {(errorInfo?.iniExtendedCode &&
          INI_ERROR_MESSAGE_MAP[errorInfo.iniExtendedCode]) ||
          UNKNOWN_ERROR_MESSAGE}
      </Text>
      <Text className="text-center whitespace-pre-line font-small1 py-4pxr text-static-mono-40">
        Error Code : {errorInfo?.code} ( {errorInfo?.iniExtendedCode} )
      </Text>
      {canRetry && (
        <>
          {isRetrying && (
            <div className="mt-12pxr">
              <Loading />
            </div>
          )}
          {!isRetrying && (
            <div
              className="flex items-center justify-center rounded-full cursor-pointer mt-12pxr h-32pxr bg-static-mono-10 py-7pxr px-14pxr"
              onClick={onClickRetry}
            >
              <Text className="font-small1 text-static-mono-90">다시시도</Text>
            </div>
          )}
        </>
      )}
    </div>
  );
}
